import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Save, Plus, Trash2, Inbox, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-zinc-400 text-xs uppercase tracking-widest font-mono">{label}</Label>
      {children}
      {hint && <p className="text-xs text-zinc-600">{hint}</p>}
    </div>
  );
}

const inputCls = "bg-black/40 border-white/15 focus-visible:ring-[#007AFF] rounded-none";

function SecurityTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Le due password nuove non coincidono");
      return;
    }
    if (next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata con successo");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Cambio password non riuscito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md space-y-6" data-testid="admin-security-form">
      <p className="text-sm text-zinc-500 border border-white/10 p-4">
        Cambia la password di accesso all'area admin. La nuova password resterà attiva anche dopo i prossimi Deploy.
      </p>
      <Field label="Password attuale">
        <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} data-testid="admin-current-password-input" />
      </Field>
      <Field label="Nuova password" hint="Minimo 8 caratteri.">
        <Input type="password" required value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="admin-new-password-input" />
      </Field>
      <Field label="Conferma nuova password">
        <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="admin-confirm-password-input" />
      </Field>
      <Button type="submit" disabled={saving} data-testid="admin-change-password-button"
        className="rounded-full bg-white text-black hover:bg-[#007AFF] hover:text-white px-8 py-5 text-sm font-semibold transition-colors">
        {saving ? "Aggiornamento..." : "Cambia password"}
      </Button>
    </form>
  );
}

function ImageUploadField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Foto caricata. Clicca 'Salva e pubblica' per applicarla.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Caricamento non riuscito. Riprova.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Field label={label} hint="Incolla un URL oppure carica un file dal tuo computer.">
      <div className="flex gap-3">
        <Input value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file-input`} />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload-button`}
          className="rounded-none border-white/20 bg-transparent hover:bg-white/10 hover:text-white shrink-0"
        >
          {uploading ? "Caricamento..." : <><Upload className="w-4 h-4 mr-2" /> Carica foto</>}
        </Button>
      </div>
    </Field>
  );
}

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const { content, refresh } = useContent();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [linesText, setLinesText] = useState("");
  const [marqueeText, setMarqueeText] = useState("");
  const [enLinesText, setEnLinesText] = useState("");
  const [enMarqueeText, setEnMarqueeText] = useState("");
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content && !form) {
      setForm(content);
      setLinesText((content.hero_lines || []).join("\n"));
      setMarqueeText((content.marquee_items || []).join("\n"));
      setEnLinesText((content.en?.hero_lines || []).join("\n"));
      setEnMarqueeText((content.en?.marquee_items || []).join("\n"));
    }
  }, [content, form]);

  useEffect(() => {
    if (user) {
      api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
      api.get("/assistant/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#007AFF] animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin" replace />;
  if (!form) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setEn = (key, value) => setForm((f) => ({ ...f, en: { ...(f.en || {}), [key]: value } }));
  const updateItem = (key, idx, field, value, ns) =>
    setForm((f) => {
      if (ns === "en") {
        const list = [...(f.en?.[key] || [])];
        list[idx] = { ...list[idx], [field]: value };
        return { ...f, en: { ...(f.en || {}), [key]: list } };
      }
      const list = [...f[key]];
      list[idx] = { ...list[idx], [field]: value };
      return { ...f, [key]: list };
    });
  const addItem = (key, ns) =>
    setForm((f) => {
      if (ns === "en") {
        return { ...f, en: { ...(f.en || {}), [key]: [...(f.en?.[key] || []), { title: "", description: "" }] } };
      }
      return { ...f, [key]: [...f[key], { title: "", description: "" }] };
    });
  const removeItem = (key, idx, ns) =>
    setForm((f) => {
      if (ns === "en") {
        return { ...f, en: { ...(f.en || {}), [key]: (f.en?.[key] || []).filter((_, i) => i !== idx) } };
      }
      return { ...f, [key]: f[key].filter((_, i) => i !== idx) };
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        hero_lines: linesText.split("\n").map((s) => s.trim()).filter(Boolean),
        marquee_items: marqueeText.split("\n").map((s) => s.trim()).filter(Boolean),
        en: {
          ...(form.en || {}),
          hero_lines: enLinesText.split("\n").map((s) => s.trim()).filter(Boolean),
          marquee_items: enMarqueeText.split("\n").map((s) => s.trim()).filter(Boolean),
        },
      };
      await api.put("/content", payload);
      await refresh();
      toast.success("Contenuti salvati e pubblicati");
    } catch (e) {
      toast.error("Salvataggio non riuscito. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const downloadCSV = (headers, rows, filename) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportMessages = () => {
    downloadCSV(
      ["Data", "Nome", "Email", "Messaggio"],
      messages.map((m) => [new Date(m.created_at).toLocaleString("it-IT"), m.name, m.email, m.message]),
      "messaggi-contatti.csv"
    );
    toast.success("CSV messaggi scaricato");
  };

  const exportQuestions = () => {
    downloadCSV(
      ["Data", "Domanda", "Risposta"],
      (stats?.recent || []).map((m) => [new Date(m.created_at).toLocaleString("it-IT"), m.message, m.reply]),
      "domande-assistente.csv"
    );
    toast.success("CSV domande scaricato");
  };

  const clearQuestions = async () => {
    if (!window.confirm("Vuoi davvero cancellare tutto lo storico delle domande all'assistente? L'operazione non è reversibile.")) return;
    try {
      const { data } = await api.delete("/assistant/messages");
      toast.success(`Storico cancellato (${data.deleted} domande rimosse)`);
      const r = await api.get("/assistant/stats");
      setStats(r.data);
    } catch (e) {
      toast.error("Cancellazione non riuscita. Riprova.");
    }
  };

  const listEditor = (key, legend, ns) => {
    const items = ns === "en" ? (form.en?.[key] || []) : (form[key] || []);
    const p = ns === "en" ? `admin-en-${key}` : `admin-${key}`;
    return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">{legend}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => addItem(key, ns)} data-testid={`${p}-add-button`}
          className="rounded-none border-white/20 bg-transparent hover:bg-white/10 hover:text-white">
          <Plus className="w-4 h-4 mr-1" /> Aggiungi
        </Button>
      </div>
      {items.map((item, idx) => (
        <div key={idx} className="border border-white/10 p-4 space-y-3" data-testid={`${p}-item-${idx}`}>
          <div className="flex items-center gap-3">
            <Input value={item.title} onChange={(e) => updateItem(key, idx, "title", e.target.value, ns)}
              placeholder="Titolo" className={inputCls} data-testid={`${p}-title-${idx}`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(key, idx, ns)}
              data-testid={`${p}-remove-${idx}`} className="text-zinc-500 hover:text-red-400 shrink-0">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <Textarea value={item.description} onChange={(e) => updateItem(key, idx, "description", e.target.value, ns)}
            placeholder="Descrizione" rows={2} className={`${inputCls} resize-none`} data-testid={`${p}-desc-${idx}`} />
        </div>
      ))}
    </div>
    );
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-display font-extrabold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 bg-[#007AFF] rounded-full" />
            {form.brand} — Admin
          </p>
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving} data-testid="admin-save-button"
              className="rounded-full bg-white text-black hover:bg-[#007AFF] hover:text-white text-sm font-semibold">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Salvataggio..." : "Salva e pubblica"}
            </Button>
            <Button onClick={doLogout} variant="outline" data-testid="admin-logout-button"
              className="rounded-full border-white/20 bg-transparent hover:bg-white/10 hover:text-white text-sm">
              <LogOut className="w-4 h-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="landing">
          <TabsList className="bg-white/[0.04] border border-white/10 rounded-none h-auto flex-wrap justify-start">
            <TabsTrigger value="landing" data-testid="admin-tab-landing" className="rounded-none data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">Landing</TabsTrigger>
            <TabsTrigger value="contact" data-testid="admin-tab-contact" className="rounded-none data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">Contatti & Email</TabsTrigger>
            <TabsTrigger value="en" data-testid="admin-tab-en" className="rounded-none data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">Inglese (EN)</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages" className="rounded-none data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">Messaggi</TabsTrigger>
            <TabsTrigger value="stats" data-testid="admin-tab-stats" className="rounded-none data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">Statistiche</TabsTrigger>
            <TabsTrigger value="security" data-testid="admin-tab-security" className="rounded-none data-[state=active]:bg-[#007AFF] data-[state=active]:text-white">Sicurezza</TabsTrigger>
          </TabsList>

          <TabsContent value="landing" className="mt-10 space-y-10">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Nome del brand"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} className={inputCls} data-testid="admin-brand-input" /></Field>
              <Field label="Occhiello hero"><Input value={form.hero_kicker} onChange={(e) => set("hero_kicker", e.target.value)} className={inputCls} data-testid="admin-hero-kicker-input" /></Field>
            </div>
            <Field label="Titolo hero" hint="Una riga per riga: ogni riga appare con l'animazione di reveal.">
              <Textarea value={linesText} onChange={(e) => setLinesText(e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-hero-lines-input" />
            </Field>
            <Field label="Sottotitolo hero"><Textarea value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-hero-subtitle-input" /></Field>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="CTA principale"><Input value={form.hero_cta_primary} onChange={(e) => set("hero_cta_primary", e.target.value)} className={inputCls} data-testid="admin-cta-primary-input" /></Field>
              <Field label="CTA secondaria"><Input value={form.hero_cta_secondary} onChange={(e) => set("hero_cta_secondary", e.target.value)} className={inputCls} data-testid="admin-cta-secondary-input" /></Field>
            </div>
            <Field label="Elementi marquee" hint="Una voce per riga.">
              <Textarea value={marqueeText} onChange={(e) => setMarqueeText(e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-marquee-input" />
            </Field>
            <Field label="Messaggio di benvenuto assistente" hint="Il fumetto parlato che l'assistente mostra in automatico ai nuovi visitatori della home.">
              <Textarea value={form.greeting || ""} onChange={(e) => set("greeting", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-greeting-input" />
            </Field>
            <div className="border-t border-white/10 pt-10 space-y-6">
              <Field label="Titolo sezione Chi sono"><Input value={form.about_title} onChange={(e) => set("about_title", e.target.value)} className={inputCls} data-testid="admin-about-title-input" /></Field>
              <Field label="Testo Chi sono"><Textarea value={form.about_body} onChange={(e) => set("about_body", e.target.value)} rows={5} className={`${inputCls} resize-none`} data-testid="admin-about-body-input" /></Field>
              <ImageUploadField label="Immagine Chi sono" value={form.about_image} onChange={(v) => set("about_image", v)} testid="admin-about-image-input" />
            </div>
            <div className="border-t border-white/10 pt-10 space-y-6">
              <Field label="Titolo sezione Servizi"><Input value={form.services_title} onChange={(e) => set("services_title", e.target.value)} className={inputCls} data-testid="admin-services-title-input" /></Field>
              {listEditor("services", "Servizi")}
            </div>
            <div className="border-t border-white/10 pt-10 space-y-6">
              <Field label="Titolo sezione Consulenze"><Input value={form.offers_title} onChange={(e) => set("offers_title", e.target.value)} className={inputCls} data-testid="admin-offers-title-input" /></Field>
              {listEditor("offers", "Consulenze")}
            </div>
          </TabsContent>

          <TabsContent value="contact" className="mt-10 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Titolo sezione contatti"><Input value={form.contact_title} onChange={(e) => set("contact_title", e.target.value)} className={inputCls} data-testid="admin-contact-title-input" /></Field>
              <Field label="Email pubblica"><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} data-testid="admin-contact-email-input" /></Field>
            </div>
            <Field label="Sottotitolo contatti"><Textarea value={form.contact_subtitle} onChange={(e) => set("contact_subtitle", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-contact-subtitle-input" /></Field>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Telefono"><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} data-testid="admin-contact-phone-input" /></Field>
              <Field label="Email destinatario richieste" hint="Le richieste inviate dal form contatti arrivano a questo indirizzo.">
                <Input value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} className={inputCls} placeholder="latuaemail@esempio.it" data-testid="admin-owner-email-input" />
              </Field>
            </div>
          </TabsContent>

          <TabsContent value="en" className="mt-10 space-y-10">
            <p className="text-sm text-zinc-500 border border-white/10 p-4">
              Traduzioni inglesi del sito. Se un campo è vuoto, chi visita in inglese vedrà la versione italiana di quel campo.
            </p>
            <Field label="Occhiello hero (EN)"><Input value={form.en?.hero_kicker || ""} onChange={(e) => setEn("hero_kicker", e.target.value)} className={inputCls} data-testid="admin-en-hero-kicker-input" /></Field>
            <Field label="Titolo hero (EN)" hint="Una riga per riga.">
              <Textarea value={enLinesText} onChange={(e) => setEnLinesText(e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-en-hero-lines-input" />
            </Field>
            <Field label="Sottotitolo hero (EN)"><Textarea value={form.en?.hero_subtitle || ""} onChange={(e) => setEn("hero_subtitle", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-en-hero-subtitle-input" /></Field>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="CTA principale (EN)"><Input value={form.en?.hero_cta_primary || ""} onChange={(e) => setEn("hero_cta_primary", e.target.value)} className={inputCls} data-testid="admin-en-cta-primary-input" /></Field>
              <Field label="CTA secondaria (EN)"><Input value={form.en?.hero_cta_secondary || ""} onChange={(e) => setEn("hero_cta_secondary", e.target.value)} className={inputCls} data-testid="admin-en-cta-secondary-input" /></Field>
            </div>
            <Field label="Elementi marquee (EN)" hint="Una voce per riga.">
              <Textarea value={enMarqueeText} onChange={(e) => setEnMarqueeText(e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-en-marquee-input" />
            </Field>
            <Field label="Messaggio di benvenuto assistente (EN)">
              <Textarea value={form.en?.greeting || ""} onChange={(e) => setEn("greeting", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid="admin-en-greeting-input" />
            </Field>
            <div className="border-t border-white/10 pt-10 space-y-6">
              <Field label="Titolo Chi sono (EN)"><Input value={form.en?.about_title || ""} onChange={(e) => setEn("about_title", e.target.value)} className={inputCls} data-testid="admin-en-about-title-input" /></Field>
              <Field label="Testo Chi sono (EN)"><Textarea value={form.en?.about_body || ""} onChange={(e) => setEn("about_body", e.target.value)} rows={6} className={`${inputCls} resize-none`} data-testid="admin-en-about-body-input" /></Field>
            </div>
            <div className="border-t border-white/10 pt-10 space-y-6">
              <Field label="Titolo sezione Servizi (EN)"><Input value={form.en?.services_title || ""} onChange={(e) => setEn("services_title", e.target.value)} className={inputCls} data-testid="admin-en-services-title-input" /></Field>
              {listEditor("services", "Servizi (EN)", "en")}
            </div>
            <div className="border-t border-white/10 pt-10 space-y-6">
              <Field label="Titolo sezione Consulenze (EN)"><Input value={form.en?.offers_title || ""} onChange={(e) => setEn("offers_title", e.target.value)} className={inputCls} data-testid="admin-en-offers-title-input" /></Field>
              {listEditor("offers", "Consulenze (EN)", "en")}
            </div>
            <div className="border-t border-white/10 pt-10 grid md:grid-cols-2 gap-6">
              <Field label="Titolo contatti (EN)"><Input value={form.en?.contact_title || ""} onChange={(e) => setEn("contact_title", e.target.value)} className={inputCls} data-testid="admin-en-contact-title-input" /></Field>
              <Field label="Sottotitolo contatti (EN)"><Input value={form.en?.contact_subtitle || ""} onChange={(e) => setEn("contact_subtitle", e.target.value)} className={inputCls} data-testid="admin-en-contact-subtitle-input" /></Field>
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-10">
            <div className="flex justify-end mb-6">
              <Button onClick={exportMessages} variant="outline" disabled={messages.length === 0} data-testid="admin-export-messages-button"
                className="rounded-none border-white/20 bg-transparent hover:bg-white/10 hover:text-white text-sm">
                <Download className="w-4 h-4 mr-2" /> Esporta CSV
              </Button>
            </div>
            {messages.length === 0 ? (
              <div className="border border-dashed border-white/15 p-16 text-center" data-testid="admin-messages-empty">
                <Inbox className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">Nessun messaggio ricevuto finora.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-messages-list">
                {messages.map((m, i) => (
                  <div key={i} className="border border-white/10 p-5" data-testid={`admin-message-${i}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-display font-bold">{m.name} <span className="text-zinc-500 font-normal text-sm">— {m.email}</span></p>
                      <p className="font-mono text-xs text-zinc-600">{new Date(m.created_at).toLocaleString("it-IT")}</p>
                    </div>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="stats" className="mt-10 space-y-10">
            <div className="flex justify-end gap-3">
              <Button onClick={clearQuestions} variant="outline" disabled={!stats || stats.total === 0} data-testid="admin-clear-questions-button"
                className="rounded-none border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300 text-sm">
                <Trash2 className="w-4 h-4 mr-2" /> Svuota storico
              </Button>
              <Button onClick={exportQuestions} variant="outline" disabled={!stats || stats.total === 0} data-testid="admin-export-questions-button"
                className="rounded-none border-white/20 bg-transparent hover:bg-white/10 hover:text-white text-sm">
                <Download className="w-4 h-4 mr-2" /> Esporta CSV
              </Button>
            </div>
            {!stats ? (
              <p className="text-zinc-500 text-sm" data-testid="admin-stats-loading">Caricamento statistiche...</p>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="border border-white/10 p-6" data-testid="admin-stats-total">
                    <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Domande totali all'assistente</p>
                    <p className="mt-3 font-display font-black text-5xl">{stats.total}</p>
                  </div>
                  <div className="border border-white/10 p-6" data-testid="admin-stats-unique">
                    <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Domande diverse</p>
                    <p className="mt-3 font-display font-black text-5xl">{stats.top_questions.length}</p>
                  </div>
                </div>

                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Domande più frequenti</p>
                  {stats.top_questions.length === 0 ? (
                    <div className="border border-dashed border-white/15 p-10 text-center text-sm text-zinc-500" data-testid="admin-stats-empty">
                      Nessuna domanda ricevuta finora.
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="admin-stats-top">
                      {stats.top_questions.map((q, i) => (
                        <div key={i} className="flex items-center justify-between gap-4 border border-white/10 px-5 py-3.5" data-testid={`admin-stats-question-${i}`}>
                          <p className="text-sm text-zinc-300">{q.text}</p>
                          <span className="font-mono text-xs text-[#007AFF] shrink-0 border border-[#007AFF]/40 rounded-full px-3 py-1">{q.count}x</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">Ultime conversazioni</p>
                  <div className="space-y-4" data-testid="admin-stats-recent">
                    {stats.recent.map((m, i) => (
                      <div key={i} className="border border-white/10 p-5" data-testid={`admin-stats-recent-${i}`}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-sm font-semibold text-zinc-200">{m.message}</p>
                          <p className="font-mono text-xs text-zinc-600">{new Date(m.created_at).toLocaleString("it-IT")}</p>
                        </div>
                        <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{m.reply}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
          <TabsContent value="security" className="mt-10">
            <SecurityTab />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
